import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BusinessService } from 'src/app/services/business/business.service';
import * as BusinessActions from './business.actions';

@Injectable()
export class BusinessEffects {
  loadBusinessesByZipCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.loadBusinessesByZipCode),
      switchMap(({ zipCode }) =>
        this.businessService.getZipCodeInfo(zipCode).pipe(
          switchMap((zipCodeInfo) => {
            console.log(zipCodeInfo);
            return this.businessService
              .getNearbyBusinessesPaginated(
                zipCodeInfo.latitude,
                zipCodeInfo.longitude,
                10,
                null,
                1,
                6
              )
              .pipe(
                map((response) =>
                  BusinessActions.loadBusinessesByZipCodeSuccess({
                    businesses: response.businessInformationEntities,
                    lastDocumentId: null, // Not used in paginated response
                    hasMore: response.hasMore,
                    totalCount: response.totalCount,
                    totalPages: response.totalPages,
                    currentPage: response.currentPage,
                    latitude: zipCodeInfo.latitude,
                    longitude: zipCodeInfo.longitude,
                  })
                ),
                catchError((error) =>
                  of(
                    BusinessActions.loadBusinessesByZipCodeFailure({
                      error: error.message || 'Failed to load businesses',
                    })
                  )
                )
              );
          }),
          catchError((error) =>
            of(
              BusinessActions.loadBusinessesByZipCodeFailure({
                error: error.message || 'Failed to get zip code information',
              })
            )
          )
        )
      )
    )
  );

  searchBusinesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.searchBusinesses),
      switchMap(({ filters }) => {
        if (!filters.zipCode) {
          return of(
            BusinessActions.searchBusinessesFailure({
              error: 'Zip code is required for search',
            })
          );
        }

        return this.businessService.getZipCodeInfo(filters.zipCode).pipe(
          switchMap((zipCodeInfo) =>
            this.businessService
              .searchBusinesses(
                filters.searchTerm || '',
                0,
                zipCodeInfo.latitude,
                zipCodeInfo.longitude,
                10
              )
              .pipe(
                map((response) =>
                  BusinessActions.searchBusinessesSuccess({
                    businesses: response.hits,
                  })
                ),
                catchError((error) =>
                  of(
                    BusinessActions.searchBusinessesFailure({
                      error: error.message || 'Failed to search businesses',
                    })
                  )
                )
              )
          ),
          catchError((error) =>
            of(
              BusinessActions.searchBusinessesFailure({
                error: error.message || 'Failed to get zip code information',
              })
            )
          )
        );
      })
    )
  );

  loadMoreBusinesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.loadMoreBusinesses),
      switchMap(({ zipCode, lastDocumentId }) =>
        this.businessService.getZipCodeInfo(zipCode).pipe(
          switchMap((zipCodeInfo) =>
            // Use paginated route - load page 2 for "load more" (assuming page 1 was already loaded)
            this.businessService
              .getNearbyBusinessesPaginated(
                zipCodeInfo.latitude,
                zipCodeInfo.longitude,
                10,
                null,
                2,
                6
              )
              .pipe(
                map((response) =>
                  BusinessActions.loadMoreBusinessesSuccess({
                    businesses: response.businessInformationEntities,
                    lastDocumentId: null, // Not used in paginated response
                    hasMore: response.hasMore,
                  })
                ),
                catchError((error) =>
                  of(
                    BusinessActions.loadMoreBusinessesFailure({
                      error: error.message || 'Failed to load more businesses',
                    })
                  )
                )
              )
          ),
          catchError((error) =>
            of(
              BusinessActions.loadMoreBusinessesFailure({
                error: error.message || 'Failed to get zip code information',
              })
            )
          )
        )
      )
    )
  );

  loadBusinessesByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.loadBusinessesByLocation),
      switchMap(({ latitude, longitude, limit }) =>
        this.businessService
          .getNearbyBusinessesPaginated(latitude, longitude, 10, null, 1, limit || 6)
          .pipe(
            map((response) =>
              BusinessActions.loadBusinessesByLocationSuccess({
                businesses: response.businessInformationEntities,
                lastDocumentId: null, // Not used in paginated response
                hasMore: response.hasMore,
              })
            ),
            catchError((error) =>
              of(
                BusinessActions.loadBusinessesByLocationFailure({
                  error: error.message || 'Failed to load nearby businesses',
                })
              )
            )
          )
      )
    )
  );

  loadMoreBusinessesByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.loadMoreBusinessesByLocation),
      switchMap(({ latitude, longitude, lastDocumentId, limit }) =>
        // Use paginated route - load page 2 for "load more" (assuming page 1 was already loaded)
        this.businessService
          .getNearbyBusinessesPaginated(latitude, longitude, 10, null, 2, limit || 6)
          .pipe(
            map((response) =>
              BusinessActions.loadMoreBusinessesSuccess({
                businesses: response.businessInformationEntities,
                lastDocumentId: null, // Not used in paginated response
                hasMore: response.hasMore,
              })
            ),
            catchError((error) =>
              of(
                BusinessActions.loadMoreBusinessesFailure({
                  error: error.message || 'Failed to load more businesses',
                })
              )
            )
          )
      )
    )
  );

  loadBusinessesByLocationPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.loadBusinessesByLocationPage),
      switchMap(({ latitude, longitude, page, lastDocumentId, limit }) =>
        // Use paginated route with the specified page number
        this.businessService
          .getNearbyBusinessesPaginated(latitude, longitude, 10, null, page, limit || 6)
          .pipe(
            map((response) =>
              BusinessActions.loadBusinessesByLocationPageSuccess({
                businesses: response.businessInformationEntities,
                lastDocumentId: null, // Not used in paginated response
                hasMore: response.hasMore,
                page,
              })
            ),
            catchError((error) =>
              of(
                BusinessActions.loadBusinessesByLocationPageFailure({
                  error: error.message || 'Failed to load businesses page',
                  page,
                })
              )
            )
          )
      )
    )
  );

  loadBusinessesByLocationPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BusinessActions.loadBusinessesByLocationPaginated),
      switchMap(({ latitude, longitude, pageNumber, limit, storeType }) =>
        this.businessService
          .getNearbyBusinessesPaginated(latitude, longitude, 10, storeType || null, pageNumber, limit)
          .pipe(
            map((response) =>
              BusinessActions.loadBusinessesByLocationPaginatedSuccess({
                businesses: response.businessInformationEntities,
                totalCount: response.totalCount,
                totalPages: response.totalPages,
                currentPage: response.currentPage,
                hasMore: response.hasMore,
                latitude,
                longitude,
              })
            ),
            catchError((error) =>
              of(
                BusinessActions.loadBusinessesByLocationPaginatedFailure({
                  error: error.message || 'Failed to load nearby businesses',
                })
              )
            )
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private businessService: BusinessService
  ) {}
}
